/**
 * PendantMap — react-leaflet map for the PalPendant Locator page.
 *
 * Uses Google base layers (Roadmap / Satellite / Terrain / Hybrid) via
 * ReactLeafletGoogleLayer + a LayersControl switcher, matching the other map
 * surfaces in the portal (e.g. FindMyDeviceTab, campus/device profiles).
 *
 * Markers are rendered declaratively from `locations`:
 *   blue pin = normal, red pin (1.2x) = alerted, highlighted = selected.
 * Selection / fly-to is driven by the `focus` prop (set by the page when a
 * location is clicked in the panel or a tracked device moves) rather than an
 * imperative ref — the inner MapFocusController reacts to it via useMap().
 *
 * Props:
 *   locations      — array of { manufacture_uuid, resident_name, latitude, longitude, create_time }
 *   alertedDevices — array of { manufacture_uuid }
 *   tzOffset       — timezone offset string for convertIsoUTCToLocal
 *   focus          — null | { uuid, latitude, longitude, zoom } — pan/zoom + open popup target
 */

import { useEffect, useRef } from 'react';
import { MapContainer, LayersControl, Marker, Popup, useMap } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import L from 'leaflet';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import { GOOGLE_MAPS_API_KEY } from '../../../../constants/mapsConfig.js';

// Continental-US default view (Leaflet uses [lat, lng] — note the order flip
// from the previous Mapbox [lng, lat]).
const DEFAULT_CENTER = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;
const FOCUS_ZOOM = 15;

const NORMAL_COLOR = '#0081C7';
const ALERTED_COLOR = '#e63757';

// ---------------------------------------------------------------------------
// Marker icon factory — Leaflet has no built-in colored/scaled pin, so we
// render an SVG teardrop as a divIcon. Passing a custom className overrides
// Leaflet's default `.leaflet-div-icon` white box (see palpendant-map-page.css).
// ---------------------------------------------------------------------------
function pendantIcon({ alerted, selected }) {
    const fill = alerted ? ALERTED_COLOR : NORMAL_COLOR;
    const scale = alerted ? 1.2 : 1;
    const w = Math.round(28 * scale);
    const h = Math.round(40 * scale);
    const wrapStyle = selected
        ? 'filter:brightness(1.15) drop-shadow(0 0 6px rgba(0,0,0,0.45));'
        : '';

    return L.divIcon({
        className: 'pm-marker',
        html: `
            <div style="${wrapStyle}">
                <svg width="${w}" height="${h}" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 0C6.27 0 0 6.27 0 14c0 9.5 14 26 14 26s14-16.5 14-26C28 6.27 21.73 0 14 0z"
                          fill="${fill}" stroke="#ffffff" stroke-width="2"/>
                    <circle cx="14" cy="14" r="5" fill="#ffffff"/>
                </svg>
            </div>`,
        iconSize: [w, h],
        iconAnchor: [w / 2, h],
        popupAnchor: [0, -h + 4],
    });
}

// ---------------------------------------------------------------------------
// MapFocusController — flies to the focused location and opens its popup
// whenever `focus` changes (new object identity on each select / tracked move).
// ---------------------------------------------------------------------------
function MapFocusController({ focus, markerRefs }) {
    const map = useMap();

    useEffect(() => {
        if (!focus || focus.latitude == null || focus.longitude == null) return;
        map.flyTo([focus.latitude, focus.longitude], focus.zoom ?? FOCUS_ZOOM, {
            duration: 1.5,
        });
        const marker = markerRefs.current[focus.uuid];
        if (marker) marker.openPopup();
    }, [focus, map, markerRefs]);

    return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PendantMap = ({ locations, alertedDevices, tzOffset, focus }) => {
    // Leaflet marker instances keyed by manufacture_uuid, for programmatic
    // popup opening from MapFocusController.
    const markerRefs = useRef({});
    const selectedUuid = focus?.uuid ?? null;

    const isAlerted = (uuid) =>
        (alertedDevices || []).some((d) => d.manufacture_uuid === uuid);

    return (
        <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom={true}
            // zIndex establishes a stacking context so Leaflet's internal panes
            // and controls (z-index up to ~1000) stay contained below the
            // floating LocationPanel (.pm-panel, z-index: 10) instead of leaking
            // above it.
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', zIndex: 0 }}
        >
            <LayersControl position="bottomright">
                <LayersControl.BaseLayer checked name="Roadmap">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_MAPS_API_KEY} type="roadmap" />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellite">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_MAPS_API_KEY} type="satellite" />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Terrain">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_MAPS_API_KEY} type="terrain" />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Hybrid">
                    <ReactLeafletGoogleLayer apiKey={GOOGLE_MAPS_API_KEY} type="hybrid" />
                </LayersControl.BaseLayer>
            </LayersControl>

            <MapFocusController focus={focus} markerRefs={markerRefs} />

            {(locations || []).map((location) => {
                const uuid = location.manufacture_uuid;
                const alerted = isAlerted(uuid);
                const timestamp = convertIsoUTCToLocal(location.create_time, tzOffset) || 'N/A';

                return (
                    <Marker
                        key={uuid}
                        position={[location.latitude, location.longitude]}
                        icon={pendantIcon({ alerted, selected: uuid === selectedUuid })}
                        ref={(inst) => {
                            if (inst) markerRefs.current[uuid] = inst;
                            else delete markerRefs.current[uuid];
                        }}
                    >
                        <Popup closeButton={true} autoClose={false} closeOnClick={false}>
                            <div className="pm-popup">
                                <div className="pm-popup__row">
                                    <div className="pm-popup__label">Resident</div>
                                    <div className="pm-popup__value">{location.resident_name || 'Unknown'}</div>
                                </div>
                                <div className="pm-popup__row">
                                    <div className="pm-popup__label">Pendant #</div>
                                    <div className="pm-popup__value">{uuid}</div>
                                </div>
                                <div className="pm-popup__row">
                                    <div className="pm-popup__label">Last Ping</div>
                                    <div className="pm-popup__value">{timestamp}</div>
                                </div>
                                {alerted ? <div className="pm-popup__alert">Alerted Device</div> : null}
                            </div>
                        </Popup>
                    </Marker>
                );
            })}
        </MapContainer>
    );
};

export default PendantMap;
